import React, { useState } from 'react';
import { motion, useMotionValue, useTransform, useSpring } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Shield, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';

const TiltCard = ({ 
  children, 
  className, 
  isActive, 
  isDimmed, 
  onMouseEnter, 
  onMouseLeave,
  cursorType,
  onClick
}) => {
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const mouseX = useSpring(x, { stiffness: 150, damping: 20 });
  const mouseY = useSpring(y, { stiffness: 150, damping: 20 });

  const rotateX = useTransform(mouseY, [-0.5, 0.5], ["5deg", "-5deg"]);
  const rotateY = useTransform(mouseX, [-0.5, 0.5], ["-5deg", "5deg"]);

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    
    const mouseXPos = e.clientX - rect.left;
    const mouseYPos = e.clientY - rect.top;

    const xPct = mouseXPos / width - 0.5;
    const yPct = mouseYPos / height - 0.5;

    x.set(xPct);
    y.set(yPct);
  };

  const handleLeave = () => {
    x.set(0);
    y.set(0);
    onMouseLeave();
  };

  // Cursor SVGs as Data URIs
  const shieldCursor = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 24 24' fill='none' stroke='%232D323E' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z'/%3E%3C/svg%3E"), auto`;
  
  const boltCursor = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 24 24' fill='none' stroke='%23ffffff' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M13 2L3 14h9l-1 8 10-12h-9l1-8z'/%3E%3C/svg%3E"), auto`;

  return (
    <motion.article
      onMouseMove={handleMouseMove}
      onMouseEnter={onMouseEnter}
      onMouseLeave={handleLeave}
      style={{
        rotateX: isActive ? rotateX : 0,
        rotateY: isActive ? rotateY : 0,
        cursor: cursorType === 'shield' ? shieldCursor : boltCursor,
        transformStyle: "preserve-3d",
      }}
      className={cn(
        "relative h-[50vh] min-h-[500px] flex flex-col justify-center px-8 md:px-16 transition-all duration-500 ease-in-out w-full md:w-1/2 overflow-hidden",
        isDimmed ? "opacity-40 grayscale blur-[1px]" : "opacity-100 grayscale-0",
        className
      )}
    >
      <div className="relative z-10" style={{ transform: "translateZ(50px)" }}>
        {children}
      </div>
    </motion.article>
  );
};

export default function SectorSplit() {
  const [activeSide, setActiveSide] = useState(null); // 'left', 'right', or null

  return (
    <section className="flex flex-col md:flex-row w-full perspective-[1000px] bg-black">
      {/* Left: Enterprise Modernisation */}
      <TiltCard
        className="bg-[#F5F3F0] text-[#2D323E]"
        isActive={activeSide === 'left'}
        isDimmed={activeSide === 'right'}
        onMouseEnter={() => setActiveSide('left')}
        onMouseLeave={() => setActiveSide(null)}
        cursorType="shield"
      >
        <span className="font-mono text-[#9e591e] tracking-widest text-sm mb-4 block font-bold">
          ESTABLISHED ENTERPRISE
        </span>
        <h3 className="font-heading font-bold text-4xl md:text-5xl mb-6 leading-tight">
          ENTERPRISE<br />MODERNISATION
        </h3>
        
        <p className="font-body text-lg mb-6 max-w-md leading-[1.6]">
          We audit, re-architect, and automate legacy workflows. Increase operational capacity without compromising your compliance obligations.
        </p>

        {/* Bullet Points */}
        <ul className="space-y-2 mb-8 font-mono text-sm text-[#2D323E]">
          <li className="flex items-start gap-2">
            <span className="text-[#9e591e] mt-1">▸</span>
            <span>Legacy Remediation & Cloud Migration</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-[#9e591e] mt-1">▸</span>
            <span>Compliance & AI Governance: Architectures for APP, APRA CPS 234, and secure internal AI adoption.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-[#9e591e] mt-1">▸</span>
            <span>Workflow Automation</span>
          </li>
        </ul>
        
        <div className="flex flex-col items-start">
          <Button 
            asChild
            variant="outline" 
            className="border-2 border-[#2D323E] text-[#2D323E] hover:bg-[#2D323E] hover:text-white rounded-[2px] uppercase font-heading tracking-wide h-12 px-8"
            aria-label="Book Compliance Audit"
          >
            <Link to="/enterprise-modernisation">Book Compliance Audit</Link>
          </Button>
          <span className="text-[10px] text-gray-500 block mt-1 tracking-wider uppercase opacity-80">View Service Page</span>
        </div>
      </TiltCard>

      {/* Right: Venture Product Engineering */}
      <TiltCard
        className="bg-[#2D323E] text-white"
        isActive={activeSide === 'right'}
        isDimmed={activeSide === 'left'}
        onMouseEnter={() => setActiveSide('right')}
        onMouseLeave={() => setActiveSide(null)}
        cursorType="bolt"
      >
        <span className="font-mono text-[#4A90E2] tracking-widest text-sm mb-4 block font-bold">
          STARTUPS & SCALE-UPS
        </span>
        <h3 className="font-heading font-bold text-4xl md:text-5xl mb-6 leading-tight text-white">
          VENTURE PRODUCT<br />ENGINEERING
        </h3>
        
        <p className="font-body text-lg mb-6 max-w-md leading-[1.6] text-white/80">
          Rapid prototyping and scalable architecture for high-growth disruptors. We bridge the gap between "MVP Speed" and "Enterprise Stability."
        </p>

        {/* Bullet Points */}
        <ul className="space-y-2 mb-8 font-mono text-sm text-white/90">
          <li className="flex items-start gap-2">
            <span className="text-[#4A90E2] mt-1">▸</span>
            <span>AI-Native Architecture: Production-ready LLM pipelines, RAG systems, and scalable inference infrastructure.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-[#4A90E2] mt-1">▸</span>
            <span>Rapid MVP Delivery</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-[#4A90E2] mt-1">▸</span>
            <span>Fractional CTO Leadership</span>
          </li>
        </ul>
        
        <div className="flex flex-col items-start">
          <Button 
            asChild
            className="bg-[#4A90E2] text-white hover:bg-[#3a7bc8] border-2 border-[#4A90E2] rounded-[2px] uppercase font-heading tracking-wide h-12 px-8 shadow-block hover:-translate-y-1 transition-transform"
            aria-label="View Engineering Protocols"
          >
            <Link to="/venture-product-engineering">View Engineering Protocols</Link>
          </Button>
          <span className="text-[10px] text-gray-400 block mt-1 tracking-wider uppercase opacity-80">View Service Page</span>
        </div>
      </TiltCard>
    </section>
  );
}
